import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import {
  addUserWine,
  getWine,
  listWineCountries,
  listWineGrapes,
  searchWines,
  searchWinesByLabel,
} from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";
import GrapeChipsField from "../components/wine/GrapeChipsField";
import { fetchMediaBlobUrl } from "../utils/mediaUrl";

function LabelPreview({ mediaUuid, previewUrl }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setFailed(false);
    setSrc("");

    (async () => {
      // Prefer local file blob while adding — it is reliable across edit/save.
      if (previewUrl) {
        if (active) setSrc(previewUrl);
        return;
      }
      if (mediaUuid) {
        try {
          const url = await fetchMediaBlobUrl(`/media/${mediaUuid}`);
          if (active) setSrc(url);
          return;
        } catch {
          if (active) setFailed(true);
          return;
        }
      }
      if (active) setFailed(true);
    })();

    return () => {
      active = false;
    };
  }, [mediaUuid, previewUrl]);

  if (failed || (!mediaUuid && !previewUrl) || !src) return null;

  return (
    <Box
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        width: "100%",
        maxWidth: 420,
        bgcolor: "grey.100",
      }}
    >
      <Box
        component="img"
        src={src}
        alt="Vinetikette"
        onError={() => setFailed(true)}
        sx={{
          display: "block",
          width: "100%",
          maxHeight: 280,
          objectFit: "contain",
        }}
      />
    </Box>
  );
}

function formatApiError(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
  }
  return err.message || fallback;
}

const COLOR_LABELS = {
  red: "Rød",
  white: "Hvid",
  rose: "Rosé",
  orange: "Orange",
  other: "Andet",
};

const COLOR_ALIASES = {
  red: "red",
  rouge: "red",
  white: "white",
  blanc: "white",
  rose: "rose",
  "rosé": "rose",
  pink: "rose",
  orange: "orange",
  other: "other",
};

const CURRENT_YEAR = new Date().getFullYear();
const VINTAGE_YEARS = Array.from({ length: CURRENT_YEAR - 1900 + 1 }, (_, i) => CURRENT_YEAR - i);

const EMPTY_MANUAL = {
  name: "",
  producer_name: "",
  color: "red",
  style: "still",
  vintage_year: "",
  is_non_vintage: true,
  country_iso: "",
  region_name: "",
  grapes: [],
  description: "",
  grapeminds_id: "",
};

function mapColor(raw) {
  const key = String(raw || "").trim().toLowerCase();
  return COLOR_ALIASES[key] || "other";
}

function stripWineName(displayName, producer) {
  let name = String(displayName || "").trim();
  const prod = String(producer || "").trim();
  if (prod && name.toLowerCase().startsWith(prod.toLowerCase())) {
    name = name.slice(prod.length).replace(/^[\s\-–,]+/, "");
  }
  name = name.replace(/\s+(?:19|20)\d{2}\s*$/, "").trim();
  return name || displayName || "";
}

function parseVintageYear(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n) && n >= 1900 && n <= CURRENT_YEAR + 1) return n;
  const match = String(value).match(/\b((?:19|20)\d{2})\b/);
  return match ? Number(match[1]) : null;
}

function selectionSummary(manual) {
  if (!manual) return "";
  const year = manual.is_non_vintage || !manual.vintage_year ? "Non-vintage" : manual.vintage_year;
  return [manual.producer_name, manual.name, year, COLOR_LABELS[manual.color] || manual.color]
    .filter(Boolean)
    .join(" · ");
}

function WineManagerAdd() {
  useDocumentTitle("Tilføj vin");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultWantlist = searchParams.get("wantlist") === "1";

  // search → edit (manual form) → save (liste/antal/noter)
  const [phase, setPhase] = useState("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [labelSearching, setLabelSearching] = useState(false);
  const [labelMediaUuid, setLabelMediaUuid] = useState("");
  const [labelPreviewUrl, setLabelPreviewUrl] = useState("");
  const [selectingId, setSelectingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const labelPreviewUrlRef = useRef("");

  const [isWantlist, setIsWantlist] = useState(defaultWantlist);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    return () => {
      if (labelPreviewUrlRef.current) {
        URL.revokeObjectURL(labelPreviewUrlRef.current);
        labelPreviewUrlRef.current = "";
      }
    };
  }, []);

  const setLabelFilePreview = (file) => {
    if (labelPreviewUrlRef.current) {
      URL.revokeObjectURL(labelPreviewUrlRef.current);
      labelPreviewUrlRef.current = "";
    }
    if (!file) {
      setLabelPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(file);
    labelPreviewUrlRef.current = url;
    setLabelPreviewUrl(url);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await listWineCountries();
        if (cancelled) return;
        setCountries(Array.isArray(c) ? c : []);
      } catch {
        /* ignore — form still usable without catalogs briefly */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveCountryIso = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";
    const upper = value.toUpperCase();
    const byIso = countries.find((c) => c.iso_code === upper || c.iso_code === value);
    if (byIso) return byIso.iso_code;
    const byName = countries.find(
      (c) => c.name.toLowerCase() === value.toLowerCase()
    );
    return byName?.iso_code || "";
  };

  const resolveKnownGrapes = async (names) => {
    const out = [];
    const seen = new Set();
    await Promise.all(
      (names || []).map(async (raw) => {
        const token = String(raw || "").trim();
        if (!token) return;
        try {
          const data = await listWineGrapes({ q: token, limit: 20 });
          const list = Array.isArray(data) ? data : [];
          const key = token.toLowerCase();
          const match =
            list.find((g) => String(g.name || "").toLowerCase() === key) ||
            list.find((g) => String(g.canonical_name || "").toLowerCase() === key);
          if (!match?.name) return;
          const sk = match.name.toLowerCase();
          if (seen.has(sk)) return;
          seen.add(sk);
          out.push(match.name);
        } catch {
          /* skip unknown */
        }
      })
    );
    return out;
  };

  const vintageSelectValue = manual.is_non_vintage ? "nv" : String(manual.vintage_year || "nv");

  const setVintageFromSelect = (value) => {
    if (value === "nv") {
      setManual((m) => ({ ...m, is_non_vintage: true, vintage_year: "" }));
      return;
    }
    setManual((m) => ({ ...m, is_non_vintage: false, vintage_year: value }));
  };

  const canContinueEdit = useMemo(
    () => Boolean(manual.name.trim() && manual.producer_name.trim()),
    [manual.name, manual.producer_name]
  );

  const phaseHint =
    phase === "search"
      ? "Søg via GrapeMinds — eller tag et foto af etiketten"
      : phase === "edit"
        ? "Tjek og ret felter (fx årgang) før du fortsætter"
        : "Samling, antal og noter";

  const runSearch = async (event) => {
    event.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      setError("Skriv mindst 3 tegn");
      return;
    }
    setSearching(true);
    setError("");
    try {
      const data = await searchWines(q, { limit: 25 });
      setResults(Array.isArray(data?.results) ? data.results : []);
      if (!data?.results?.length) setError("Ingen vine fundet. Prøv et andet søgeord.");
    } catch (err) {
      setError(formatApiError(err, "Søgning fejlede"));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const runLabelSearch = async (file) => {
    if (!file) return;
    setLabelFilePreview(file);
    setLabelSearching(true);
    setError("");
    try {
      const data = await searchWinesByLabel(file, { limit: 25 });
      const nextQuery = String(data?.query || "").trim();
      setQuery(nextQuery);
      setResults(Array.isArray(data?.results) ? data.results : []);
      const mediaId = data?.media_uuid ? String(data.media_uuid) : "";
      setLabelMediaUuid(mediaId);
      // Keep local file preview as reliable display source through edit/save
      if (!data?.results?.length) {
        setError("Ingen vine fundet. Ret søgeteksten og søg igen.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.query != null) setQuery(String(detail.query || ""));
        if (detail.media_uuid) setLabelMediaUuid(String(detail.media_uuid));
        if (detail.ocr && !detail.query) {
          const parts = ["producer", "wine_name", "appellation", "vintage", "classification"]
            .map((k) => detail.ocr[k])
            .filter(Boolean);
          if (parts.length) setQuery(parts.join(" "));
        }
      }
      setResults([]);
      setError(formatApiError(err, "Etiketsøgning fejlede"));
    } finally {
      setLabelSearching(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const applyDetailToManual = async (detail, fallbackHit = {}) => {
    const producer = detail.producer || fallbackHit.producer || "";
    const display = detail.name || fallbackHit.name || "";
    const year = parseVintageYear(detail.vintage || fallbackHit.vintage);
    const rawGrapes = Array.isArray(detail.grapes)
      ? detail.grapes
      : String(detail.grape || "")
          .split(",")
          .map((g) => g.trim())
          .filter(Boolean);
    const grapes = await resolveKnownGrapes(rawGrapes);
    setManual({
      name: stripWineName(display, producer),
      producer_name: producer,
      color: mapColor(detail.color || fallbackHit.color),
      style: "still",
      vintage_year: year ? String(year) : "",
      is_non_vintage: !year,
      country_iso: resolveCountryIso(detail.country),
      region_name: detail.region_name || "",
      grapes,
      description: "",
      grapeminds_id: detail.id != null ? String(detail.id) : String(fallbackHit.id || ""),
    });
  };

  const pickSearchHit = async (hit) => {
    setSelectingId(String(hit.id));
    setError("");
    try {
      let detail = null;
      try {
        detail = await getWine(hit.id);
      } catch {
        detail = null;
      }
      await applyDetailToManual(detail || {}, hit);
      setPhase("edit");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke hente vin");
    } finally {
      setSelectingId("");
    }
  };

  const startBlankManual = () => {
    setManual(EMPTY_MANUAL);
    setError("");
    setPhase("edit");
  };

  const continueToSave = (event) => {
    event.preventDefault();
    if (!canContinueEdit) {
      setError("Navn og producent er påkrævet");
      return;
    }
    setError("");
    setPhase("save");
  };

  const goBack = () => {
    setError("");
    if (phase === "save") {
      setPhase("edit");
      return;
    }
    if (phase === "edit") {
      setPhase("search");
    }
  };

  const saveWine = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const year = manual.is_non_vintage
        ? null
        : manual.vintage_year
          ? Number(manual.vintage_year)
          : null;
      await addUserWine({
        manual: {
          name: manual.name.trim(),
          producer_name: manual.producer_name.trim(),
          color: manual.color,
          style: manual.style,
          vintage_year: Number.isFinite(year) ? year : null,
          is_non_vintage: Boolean(manual.is_non_vintage) || !year,
          country_iso: manual.country_iso || null,
          region_name: manual.region_name.trim() || null,
          grapes: Array.isArray(manual.grapes) ? manual.grapes : [],
          description: manual.description.trim() || null,
          grapeminds_id: manual.grapeminds_id.trim() || null,
        },
        quantity: isWantlist
          ? 0
          : (() => {
              const n = Number(quantity);
              return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 1;
            })(),
        notes: notes.trim() || null,
        is_wantlist: isWantlist,
        in_collection: !isWantlist,
        label_media_uuid: labelMediaUuid || null,
      });
      navigate(isWantlist ? "/tools/wine-manager?tab=wantlist" : "/tools/wine-manager");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke tilføje vin");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 720 }}>
      {phase === "search" ? (
        <Button component={RouterLink} to="/tools/wine-manager" variant="text" sx={{ px: 0, mb: 1 }}>
          Tilbage
        </Button>
      ) : (
        <Button type="button" variant="text" onClick={goBack} sx={{ px: 0, mb: 1 }}>
          {phase === "edit" ? "← Til søgning" : "← Ret vin"}
        </Button>
      )}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Tilføj vin
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {phaseHint}
      </Typography>

      {(phase === "edit" || phase === "save") && (labelPreviewUrl || labelMediaUuid) ? (
        <LabelPreview mediaUuid={labelMediaUuid} previewUrl={labelPreviewUrl} />
      ) : null}

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {phase === "search" ? (
        <Box>
          <Stack component="form" onSubmit={runSearch} direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søg vin…"
              disabled={labelSearching}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => runLabelSearch(e.target.files?.[0])}
            />
            <IconButton
              color="primary"
              aria-label="Scan etikette"
              disabled={searching || labelSearching}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {labelSearching ? <CircularProgress size={18} /> : <PhotoCameraIcon />}
            </IconButton>
            <Button type="submit" variant="contained" disabled={searching || labelSearching}>
              {searching ? <CircularProgress size={18} /> : "Søg"}
            </Button>
          </Stack>
          <List
            sx={{
              maxHeight: 360,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 2,
            }}
          >
            {results.map((hit) => (
              <ListItemButton
                key={hit.id}
                disabled={Boolean(selectingId) || labelSearching}
                onClick={() => pickSearchHit(hit)}
              >
                <ListItemText
                  primary={hit.name || "Vin"}
                  secondary={
                    selectingId === String(hit.id)
                      ? "Henter detaljer…"
                      : [hit.producer, hit.color].filter(Boolean).join(" · ")
                  }
                />
              </ListItemButton>
            ))}
          </List>
          <Button variant="outlined" onClick={startBlankManual} disabled={labelSearching}>
            Indtast manuelt i stedet
          </Button>
        </Box>
      ) : null}

      {phase === "edit" ? (
        <Box component="form" onSubmit={continueToSave}>
          {manual.grapeminds_id ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Fra GrapeMinds (id {manual.grapeminds_id}) — ret gerne årgang og øvrige felter
            </Typography>
          ) : null}
          <Stack spacing={1.5}>
            <TextField
              required
              size="small"
              label="Vin"
              value={manual.name}
              onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
            />
            <TextField
              required
              size="small"
              label="Producent"
              value={manual.producer_name}
              onChange={(e) => setManual((m) => ({ ...m, producer_name: e.target.value }))}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                select
                size="small"
                label="Farve"
                value={manual.color}
                onChange={(e) => setManual((m) => ({ ...m, color: e.target.value }))}
                fullWidth
              >
                <MenuItem value="red">Rød</MenuItem>
                <MenuItem value="white">Hvid</MenuItem>
                <MenuItem value="rose">Rosé</MenuItem>
                <MenuItem value="orange">Orange</MenuItem>
                <MenuItem value="other">Andet</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Stil"
                value={manual.style}
                onChange={(e) => setManual((m) => ({ ...m, style: e.target.value }))}
                fullWidth
              >
                <MenuItem value="still">Stille</MenuItem>
                <MenuItem value="sparkling">Mousserende</MenuItem>
                <MenuItem value="fortified">Stærkvin</MenuItem>
                <MenuItem value="dessert">Dessert</MenuItem>
                <MenuItem value="other">Andet</MenuItem>
              </TextField>
            </Stack>
            <TextField
              select
              size="small"
              label="Årgang"
              value={vintageSelectValue}
              onChange={(e) => setVintageFromSelect(e.target.value)}
              fullWidth
            >
              <MenuItem value="nv">Non-vintage</MenuItem>
              {VINTAGE_YEARS.map((year) => (
                <MenuItem key={year} value={String(year)}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Land"
              value={manual.country_iso || ""}
              onChange={(e) => setManual((m) => ({ ...m, country_iso: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">
                <em>Vælg land</em>
              </MenuItem>
              {countries.map((c) => (
                <MenuItem key={c.iso_code} value={c.iso_code}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Region"
              value={manual.region_name}
              onChange={(e) => setManual((m) => ({ ...m, region_name: e.target.value }))}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Druer
              </Typography>
              <GrapeChipsField
                value={manual.grapes}
                onChange={(grapes) => setManual((m) => ({ ...m, grapes }))}
              />
            </Box>
            <TextField
              size="small"
              label="Beskrivelse"
              value={manual.description}
              onChange={(e) => setManual((m) => ({ ...m, description: e.target.value }))}
              multiline
              minRows={2}
            />
            <Button type="submit" variant="contained" disabled={!canContinueEdit}>
              Fortsæt
            </Button>
          </Stack>
        </Box>
      ) : null}

      {phase === "save" ? (
        <Box component="form" onSubmit={saveWine}>
          <Box
            sx={{
              mb: 2,
              py: 1.25,
              px: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Valgt vin
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selectionSummary(manual)}
            </Typography>
          </Box>
          <Stack spacing={1.5}>
            <TextField
              select
              size="small"
              label="Liste"
              value={isWantlist ? "wantlist" : "collection"}
              onChange={(e) => setIsWantlist(e.target.value === "wantlist")}
            >
              <MenuItem value="collection">Samling</MenuItem>
              <MenuItem value="wantlist">Ønskeliste</MenuItem>
            </TextField>
            {!isWantlist ? (
              <TextField
                type="number"
                size="small"
                label="Antal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ min: 0 }}
              />
            ) : null}
            <TextField
              size="small"
              label="Noter"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
            />
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button type="button" variant="text" onClick={goBack} disabled={saving}>
                Tilbage
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? "Gemmer…" : "Tilføj"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

export default WineManagerAdd;
